import {
  useNotify,
  useRedirect,
  useRefresh,
  useDataProvider,
} from 'react-admin';
import { useCallback } from 'react';

/**
 * Custom hook that provides mutation options with automatic refresh handling
 *
 * @param {Object} options - Configuration options
 * @param {string} options.successMessage - Message to show on success
 * @param {string} options.errorMessage - Optional custom error message
 * @param {string} options.redirectTo - Where to redirect after success (e.g., 'list')
 * @param {string} options.resource - Resource name for redirect
 * @param {Function} options.onSuccessCallback - Optional additional success callback
 * @param {Function} options.onErrorCallback - Optional additional error callback
 *
 * @returns {Object} Mutation options for react-admin Create/Edit components
 *
 * @example
 * const mutationOptions = useMutationWithRefresh({
 *   successMessage: 'Role created successfully',
 *   redirectTo: 'list',
 *   resource: 'roles'
 * });
 *
 * return <Create mutationOptions={mutationOptions}>...</Create>
 */
export const useMutationWithRefresh = ({
  successMessage,
  errorMessage,
  redirectTo = 'list',
  resource,
  onSuccessCallback,
  onErrorCallback,
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const onSuccess = useCallback(
    async (data) => {
      notify(successMessage, { type: 'success' });

      if (onSuccessCallback) {
        await onSuccessCallback(data);
      }
    },
    [notify, successMessage, onSuccessCallback],
  );

  const onError = useCallback(
    (error) => {
      const message =
        errorMessage || error?.reason || error?.message || 'An error occurred';

      notify(message, { type: 'error' });

      if (onErrorCallback) {
        onErrorCallback(error);
      }
    },
    [notify, errorMessage, onErrorCallback],
  );

  const onSettled = useCallback(async () => {
    // Always refresh after mutation settles (success or error)
    try {
      // Force a hard refresh by invalidating the query cache
      // This ensures React Admin refetches the data immediately
      refresh();

      // For resources that might be cached, also try to invalidate specific queries
      if (resource && dataProvider.getList) {
        // Trigger a lightweight query to force cache invalidation
        dataProvider
          .getList(resource, {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
          })
          .catch(() => {
            // Ignore errors from this invalidation attempt
          });
      }

      // Wait longer for refresh and cache invalidation to complete
      await new Promise((resolve) => setTimeout(resolve, 250));

      // Only redirect after refresh has had time to complete
      if (redirectTo && resource) {
        redirect(redirectTo, resource);
      }
    } catch (error) {
      // Still redirect even if refresh fails
      if (redirectTo && resource) {
        redirect(redirectTo, resource);
      }
    }
  }, [refresh, redirect, redirectTo, resource, dataProvider]);

  return {
    onSuccess,
    onError,
    onSettled,
  };
};

/**
 * Simplified version for create operations
 */
export const useCreateWithRefresh = (resource, options = {}) => {
  return useMutationWithRefresh({
    successMessage:
      options.successMessage || `${resource} created successfully`,
    redirectTo: 'list',
    resource,
    ...options,
  });
};

/**
 * Simplified version for edit operations
 */
export const useEditWithRefresh = (resource, options = {}) => {
  return useMutationWithRefresh({
    successMessage:
      options.successMessage || `${resource} updated successfully`,
    redirectTo: options.redirectTo !== undefined ? options.redirectTo : 'list',
    resource,
    ...options,
  });
};
